import { Link } from 'react-router-dom';

function Privacy() {
  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8">
        <div className="mb-6">
          <Link to="/login" className="text-brand-coral hover:text-brand-coral-dark text-sm">
            ← Back to Sign In
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-brand-black mb-1">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">
          Last updated: September 6, 2026 — The Tighlman Group LLC ·{' '}
          <Link to="/terms" className="text-brand-coral hover:text-brand-coral-dark">
            Terms of Use
          </Link>
        </p>

        <div className="space-y-6 text-sm text-gray-700 leading-relaxed">

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">1. Scope and Acceptance</h2>
            <p>
              This Privacy Policy (“Policy”) describes how The Tighlman Group LLC, an Arizona
              limited liability company (“The Tighlman Group,” “we,” “us,” or “our”), collects,
              uses, discloses, and otherwise processes information in connection with the SWYFT
              Restoration scheduler available at swyftschedule.com and www.swyftschedule.com
              (the “System”).
            </p>
            <p className="mt-3">
              The System is an internal business application licensed for use by authorized
              Swyft Restoration personnel to create, schedule, and manage field-restoration jobs.
              It is not a public marketing website and is not intended for general consumer use.
            </p>
            <p className="mt-3">
              This Policy addresses information handling only. Rules governing use of the System
              are set out separately in our{' '}
              <Link to="/terms" className="underline hover:text-gray-900">
                Terms of Use
              </Link>
              , which apply alongside this Policy.
            </p>
            <p className="mt-3">
              By accessing or using the System, you acknowledge that you have read, understood,
              and agree to be bound by this Policy. If you do not agree, you must immediately
              discontinue use of the System.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">2. Information We Collect</h2>
            <p>
              We collect information necessary to operate the System for the client’s internal
              field-scheduling operations. Categories include:
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-2">
              <li>
                <strong>Account and authentication information.</strong> When an authorized user
                signs in, we process the email address and credentials associated with that
                account through our authentication provider (Supabase Auth). We also process
                session information needed to keep the user signed in and to reset passwords.
              </li>
              <li>
                <strong>Business and operational data entered by authorized managers.</strong>{' '}
                Authorized users may enter and store job and property information used to
                schedule field work. This may include property names and addresses; unit numbers;
                job types, urgency, deadlines, and special notes; task checklists; property
                manager and maintenance-supervisor contact information; property and billing
                email addresses; phone numbers; gate codes; access notes; and other notes or
                details the client chooses to record about customers, properties, or jobs.
              </li>
              <li>
                <strong>Activity and audit logs.</strong> The System records activity for
                security, compliance, and operational purposes. Logs may include the action
                taken (for example, creating, updating, or deleting a job or property, or
                signing in), the user identifier and email associated with the action, the
                type and identifier of the affected record, related contextual details (such
                as a property name, scheduled date, or unit number), and the time of the event.
              </li>
              <li>
                <strong>Technical and infrastructure information.</strong> Our hosting and
                database providers may process limited technical information generated in the
                ordinary operation of the System, such as connection and security logs needed
                to deliver the service, authenticate users, and detect abuse.
              </li>
            </ul>
            <p className="mt-3">
              We do not request biometric data, government-issued identifiers, or payment-card
              data through the System. Do not submit such information unless the client has a
              specific operational need and has instructed you to do so.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">3. How We Use Information</h2>
            <p>
              We use information to operate the internal field scheduler for the client,
              including:
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-2">
              <li>authenticating authorized users and managing sessions and password resets;</li>
              <li>creating, scheduling, reordering, printing, and otherwise managing jobs and properties;</li>
              <li>maintaining an audit trail of changes and sign-in events;</li>
              <li>operating, maintaining, securing, and troubleshooting the System;</li>
              <li>communicating with the client about the System;</li>
              <li>
                preventing, detecting, investigating, and responding to unauthorized access,
                abuse, or violations of our terms or applicable law;
              </li>
              <li>complying with legal obligations; and</li>
              <li>establishing, exercising, or defending legal rights and claims.</li>
            </ul>
            <p className="mt-3">
              The Tighlman Group LLC and its authorized personnel may access System data and
              the application as needed to host, maintain, update, secure, troubleshoot, and
              support the System for the client. This access continues for as long as we
              provide hosting or support.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">4. How We Share Information</h2>
            <p>
              We do not sell personal information. We share information only as described below
              or with the client’s or your consent:
            </p>
            <ul className="list-disc pl-5 mt-3 space-y-2">
              <li>
                <strong>Service providers.</strong> We engage infrastructure and hosting
                providers to operate the System on our behalf. These currently include Supabase
                (authentication and database) and Vercel (application hosting), and may include
                other vendors that perform similar hosting, email-delivery, or infrastructure
                functions. These providers may process information solely to perform their
                services.
              </li>
              <li>
                <strong>Professional advisors.</strong> Attorneys, accountants, auditors,
                insurers, and consultants who provide professional services to us.
              </li>
              <li>
                <strong>Legal requirements.</strong> When we believe in good faith that
                disclosure is necessary to comply with applicable law, legal process, or a
                regulatory request, or to protect the rights, property, or safety of The
                Tighlman Group, the client, our personnel, or the public.
              </li>
              <li>
                <strong>Business transfers.</strong> In connection with a merger, acquisition,
                financing, reorganization, sale of assets, bankruptcy, or similar transaction,
                information may be transferred or disclosed to potential or actual successors.
              </li>
              <li>
                <strong>The Tighlman Group.</strong> The Tighlman Group LLC and its authorized
                personnel may access System data and the application as needed to host,
                maintain, update, secure, troubleshoot, and support the System for the client.
                This access continues for as long as we provide hosting or support.
              </li>
              <li>
                <strong>The client.</strong> Because the System is an internal tool for Swyft
                Restoration, authorized client personnel may access business data and activity
                logs stored in the System.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">5. Data Retention</h2>
            <p>
              We retain information for as long as necessary to fulfill the purposes described
              in this Policy, including to satisfy legal, accounting, contractual, audit,
              dispute-resolution, or enforcement requirements. We may retain information in
              backups, archives, or disaster-recovery systems even after deletion from active
              systems.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">6. Data Security</h2>
            <p>
              The System is delivered over HTTPS. We take commercially reasonable measures
              designed to protect the information we hold from loss, misuse, and unauthorized
              access, disclosure, alteration, or destruction. However, no method of
              transmission over the Internet or method of electronic storage is completely
              secure. We cannot and do not guarantee the absolute security of any information,
              and you transmit information to us at your own risk.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">7. Your Rights and Choices</h2>
            <p>
              Depending on your jurisdiction, you may have rights regarding personal information
              about you, such as the right to access, correct, delete, restrict, or object to
              processing, or to receive a copy of your personal information in a portable
              format. Authorized users and individuals whose information is stored in the
              System may exercise any such rights by emailing us using the contact details in
              Section 10. We will respond as required by applicable law and may require
              verification of your identity before fulfilling a request. Some requests may
              also require coordination with the client, because the System stores business
              records on the client’s behalf.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">8. Children</h2>
            <p>
              The System is not directed to children under the age of thirteen (13), and we do
              not knowingly collect personal information from children under thirteen (13). If
              you believe a child has provided us with personal information, please contact us
              and we will take reasonable steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">9. Changes to This Policy</h2>
            <p>
              We may update this Policy at any time and for any reason. When we do, we will
              revise the “Last Updated” date above. Your continued use of the System after any
              change constitutes acceptance of the updated Policy. We encourage you to review
              this Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-gray-900 mb-2">10. Contact</h2>
            <p>
              For privacy questions, access, correction, or deletion requests, or other notices
              required under this Policy, contact The Tighlman Group LLC by email at{' '}
              <a href="mailto:contact@tighlmangroup.com" className="underline hover:text-gray-900">
                contact@tighlmangroup.com
              </a>
              {' '}or{' '}
              <a href="mailto:zachary.williams@tighlmangroup.com" className="underline hover:text-gray-900">
                zachary.williams@tighlmangroup.com
              </a>.
            </p>
          </section>

        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap items-center gap-4">
          <Link
            to="/login"
            className="bg-brand-coral text-white px-4 py-2 rounded-md hover:bg-brand-coral-dark text-sm"
          >
            Back to Sign In
          </Link>
          <Link to="/terms" className="text-brand-coral hover:text-brand-coral-dark text-sm">
            Terms of Use
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
